import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import CultivarProfileView from '../cultivar/CultivarProfileView';
import nurseryService from '../../services/nursery';
import { errorMessage } from '../../utils/apiResponse';
import { profileHasContent } from '../../utils/cultivarProfile';
import type { CultivarInfoSubject, NurseryCultivarInfo } from '../../types/nursery';

/**
 * What to tell a customer about something the nursery sells: the description
 * and talking points of a stock item's cultivar and its rootstock, or of every
 * member of an umbrella product. Read-only; the text is written on the
 * cultivar's own page.
 */

export interface CultivarInfoDialogProps {
  subject: CultivarInfoSubject;
  /** What was tapped, as the list names it. */
  title: string;
  onClose: () => void;
}

export function CultivarInfoDialog({ subject, title, onClose }: CultivarInfoDialogProps) {
  const [cultivars, setCultivars] = useState<NurseryCultivarInfo[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const stockItemId = 'stock_item_id' in subject ? subject.stock_item_id : null;
  const productId = 'product_id' in subject ? subject.product_id : null;

  useEffect(() => {
    let cancelled = false;
    setCultivars(null);
    setError(null);
    nurseryService.getCultivarInfo(productId != null ? { product_id: productId } : { stock_item_id: stockItemId as number })
      .then((res) => {
        if (cancelled) return;
        if (res.success && res.data) setCultivars(res.data.cultivars);
        else setError(errorMessage(res, 'Could not load the description'));
      })
      .catch((e) => { if (!cancelled) setError(errorMessage(e, 'Could not load the description')); });
    return () => { cancelled = true; };
  }, [stockItemId, productId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const written = (cultivars ?? []).filter((c) => c.description || profileHasContent(c.sales_profile));
  const unwritten = (cultivars ?? []).filter((c) => !written.includes(c));

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4" role="dialog" aria-modal="true" aria-labelledby="cultivar-info-title">
      <div className="bg-white w-full sm:max-w-2xl rounded-t-2xl sm:rounded-xl shadow-xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-3 px-5 pt-5">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">What to tell a customer</p>
            <h2 id="cultivar-info-title" className="text-lg font-semibold text-neutral-900 break-words">{title}</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="p-1 rounded hover:bg-neutral-100 flex-shrink-0">
            <X className="h-5 w-5 text-neutral-500" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-5">
          {error && <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 break-words">{error}</div>}
          {!cultivars && !error && <div className="h-32 bg-neutral-100 rounded-lg animate-pulse" />}

          {written.map((c) => (
            <section key={c.cultivar_id} className="min-w-0 border-t border-neutral-100 pt-4 first:border-t-0 first:pt-0">
              <h3 className="text-base font-semibold text-neutral-900 break-words">
                {c.name}
                <span className="ml-2 text-xs font-normal text-neutral-500">
                  {c.role === 'rootstock' ? 'Rootstock' : c.crop_type_name}
                </span>
              </h3>
              {c.description && <p className="mt-1 text-sm text-neutral-700 break-words">{c.description}</p>}
              {profileHasContent(c.sales_profile) && (
                <div className="mt-3"><CultivarProfileView profile={c.sales_profile} /></div>
              )}
            </section>
          ))}

          {cultivars && unwritten.length > 0 && (
            <p className="text-sm text-neutral-600 break-words">
              Nothing has been written about {unwritten.map((c) => c.name).join(', ')} yet.
              A Cultivar Admin can have it researched from the cultivar's page.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
